/* eslint-disable @typescript-eslint/no-explicit-any */
import * as React from "react";
import { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Chip,
  CircularProgress,
  AlertColor,
} from "@mui/material";
import { Notify } from "./Notify";
import { ITfidTags } from "../services/registry/TfidTagsService";

export interface TfidTagEditorDialogProps {
  visible: boolean;
  tfid: string;
  initialData: ITfidTags | undefined;
  onSave: (tags: string[], label?: string, env?: string) => Promise<void>;
  onCancel: () => void;
}

export const TfidTagEditorDialog = ({ visible, tfid, initialData, onSave, onCancel }: TfidTagEditorDialogProps) => {
  const [loading, setLoading] = useState(false);
  const [alertMessage, setAlertMessage] = useState<string>("");
  const [alertSeverity, setAlertSeverity] = useState<AlertColor>("info");

  const [label, setLabel] = useState<string>("");
  const [tags, setTags] = useState<string[]>([]);
  const [env, setEnv] = useState<string>("");
  const [newTagValue, setNewTagValue] = useState<string>("");

  useEffect(() => {
    if (visible) {
      setLabel(initialData?.label ?? "");
      setTags(initialData?.tags ? [...initialData.tags] : []);
      setEnv(initialData?.env ?? "");
      setNewTagValue("");
      setAlertMessage("");
    }
  }, [visible, initialData]);

  const handleAddTag = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter" && newTagValue.trim() !== "") {
      event.preventDefault();
      if (!tags.includes(newTagValue.trim())) {
        setTags([...tags, newTagValue.trim()]);
      }
      setNewTagValue("");
    }
  };

  const handleDeleteTag = (index: number) => {
    const updated = [...tags];
    updated.splice(index, 1);
    setTags(updated);
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      await onSave(tags, label || undefined, env || undefined);
    } catch (e: any) {
      setAlertMessage(e.debugMessage || "Failed to save TFID tags.");
      setAlertSeverity("error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Dialog open={visible} onClose={onCancel} maxWidth="sm" fullWidth>
        <DialogTitle>Edit TFID Tags</DialogTitle>
        <DialogContent>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
              <TextField label="TFID" value={tfid} InputProps={{ readOnly: true }} fullWidth variant="filled" />
              <TextField
                label="Label"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                fullWidth
                placeholder="My data"
              />
              <Box>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: 1 }}>
                  {tags.map((tag, index) => (
                    <Chip key={index} label={tag} onDelete={() => handleDeleteTag(index)} size="small" />
                  ))}
                </Box>
                <TextField
                  label="Add Tag"
                  value={newTagValue}
                  onChange={(e) => setNewTagValue(e.target.value)}
                  onKeyDown={handleAddTag}
                  size="small"
                  fullWidth
                  helperText="Press Enter to add a tag"
                />
              </Box>
              <TextField
                label="Environment"
                value={env}
                onChange={(e) => setEnv(e.target.value)}
                fullWidth
                placeholder="dev1"
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={onCancel} color="error" disabled={loading}>
            Cancel
          </Button>
          <Button variant="outlined" onClick={handleSave} disabled={loading}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
      {alertMessage && <Notify severity={alertSeverity} message={alertMessage} />}
    </>
  );
};
